import { ChangeEvent, useCallback, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Box, Slider, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { isImage, normalizeToBlob } from '../../../utils/image';
import { uploadFiles } from '../../../services/global';
import useUpdateBusiness from '../../../hooks/businesses/useUpdateBusiness';
import useGetBookingSite from '../../../hooks/businesses/useGetBookingSite';
import { updateEmployeeProfileImage } from '../../../services/employee';
import SmallModal from '../../global/SmallModal';
import { CANVAS, LINE_MID, resetButton } from './constants';

/** Alternating tile so a transparent PNG reads honestly behind the crop. */
const CHECKER = `repeating-conic-gradient(${CANVAS} 0% 25%, #ffffff 0% 50%) 50% / 16px 16px`;

/** The badge renders at 56-76 CSS px, so 512 is ample headroom for 3x. */
const MAX_OUTPUT = 512;
/** Below this it looks soft even at badge size. */
const MIN_CROP = 192;

interface Props {
  businessId: string;
  /** Present for independents — their booking site is the artist page. */
  employeeId: string | null;
  logoUrl: string | null;
  onSaved: () => void;
}

async function cropToSquare(src: string, area: Area): Promise<string> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = reject;
    element.src = src;
  });

  // Measured on the crop, not the whole image — a tight crop is what blurs.
  if (area.width < MIN_CROP || area.height < MIN_CROP) {
    throw new Error(
      `Zoom out a little — the selected area is too small to stay sharp.`,
    );
  }

  const output = Math.min(MAX_OUTPUT, Math.round(area.width));

  const canvas = document.createElement('canvas');
  canvas.width = output;
  canvas.height = output;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process that image.');

  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    output,
    output,
  );
  // PNG, not JPEG: logos arrive with transparent backgrounds, and JPEG has no
  // alpha, so every clear pixel would encode as black.
  return canvas.toDataURL('image/png');
}

function LogoSection({ businessId, employeeId, logoUrl, onSaved }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const cropPx = area ? Math.round(Math.min(area.width, area.height)) : 0;
  const stored = Math.min(MAX_OUTPUT, cropPx);
  const quality = !area
    ? { label: '', color: 'text.secondary', canSave: false }
    : cropPx < MIN_CROP
    ? {
        label: 'Zoom out — too small to stay sharp',
        color: 'error.dark',
        canSave: false,
      }
    : {
        label: `Saves at ${stored} × ${stored}px`,
        color: 'text.tertiary',
        canSave: true,
      };

  const { mutateAsync: updateBusiness } = useUpdateBusiness();
  const { refetch } = useGetBookingSite();

  // Object URLs are held by the document until revoked.
  const closeCropper = () => {
    setSource(current => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setArea(pixels);
  }, []);

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    // Cleared so picking the same file twice still fires a change.
    input.value = '';
    if (!file) return;

    if (!isImage(file)) {
      toast.error('Choose a JPG, PNG, WEBP or HEIC image.');
      return;
    }

    try {
      // normalizeToBlob handles HEIC without processImage's 1200px downscale,
      // which was rejecting sources that had plenty of pixels to crop from.
      setSource(URL.createObjectURL(await normalizeToBlob(file)));
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not read that image.',
      );
    }
  };

  // EmployeeHeader prefers profileImage, so an independent's logo must go there.
  const save = async (url: string | null) => {
    if (employeeId) {
      await updateEmployeeProfileImage(employeeId, url || '');
    } else {
      await updateBusiness({ thumbnailImage: url || '' });
    }
    // logoUrl comes from the booking-site query, which neither write touches.
    await refetch();
    onSaved();
  };

  const onConfirm = async () => {
    if (!source || !area) return;
    setIsSaving(true);
    try {
      const cropped = await cropToSquare(source, area);
      const [url] = await uploadFiles([
        {
          id: businessId,
          url: cropped,
          path: employeeId
            ? `employees/${employeeId}/logo/${Date.now()}`
            : `businesses/${businessId}/logo/${Date.now()}`,
        },
      ]);
      await save(url);
      closeCropper();
      toast.success('Logo updated!');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not upload that image.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Box
          sx={{
            width: 76,
            height: 76,
            flex: 'none',
            borderRadius: '50%',
            border: '1px solid #000',
            background: logoUrl ? `url(${logoUrl}) center/cover` : CANVAS,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.tertiary',
            typography: 'caption',
            fontWeight: 600,
          }}>
          {!logoUrl && 'LOGO'}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.45, color: 'text.secondary' }}>
            Shown in a circle on your booking site. Bigger is better, but
            anything reasonably sharp works.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
            }}>
            <Box
              component="button"
              type="button"
              onClick={() => inputRef.current?.click()}
              sx={{
                ...resetButton,
                padding: '9px 16px',
                borderRadius: '50px',
                background: '#000',
                color: 'common.white',
                typography: 'h5',
                fontWeight: 700,
              }}>
              {logoUrl ? 'Replace' : 'Upload'}
            </Box>
            {logoUrl && (
              <Box
                component="button"
                type="button"
                onClick={() => save(null)}
                sx={{
                  ...resetButton,
                  padding: '9px 12px',
                  borderRadius: '50px',
                  color: 'error.dark',
                  typography: 'h5',
                  fontWeight: 700,
                }}>
                Remove
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        hidden
        onChange={onFileChange}
      />

      <SmallModal
        open={!!source}
        onClose={closeCropper}
        title="Crop your logo"
        width="480px"
        footer={{
          button: {
            type: 'button',
            text: 'Use this logo',
            loading: isSaving,
            disabled: !quality.canSave,
            onClick: onConfirm,
          },
        }}>
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1',
              maxHeight: '44vh',
              borderRadius: '10px',
              overflow: 'hidden',
              border: `1px solid ${LINE_MID}`,
              background: CHECKER,
            }}>
            {source && (
              <Cropper
                image={source}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                restrictPosition
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: { background: 'transparent' },
                  cropAreaStyle: { border: 'none', color: 'rgba(0,0,0,0.55)' },
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="button"
              type="button"
              aria-label="Zoom out"
              onClick={() => setZoom(Math.max(1, zoom - 0.15))}
              sx={{
                ...resetButton,
                color: 'text.secondary',
                width: 20,
                typography: 'body1',
              }}>
              −
            </Box>
            <Slider
              value={zoom}
              min={1}
              max={4}
              step={0.01}
              size="small"
              onChange={(_, value) => setZoom(value as number)}
              aria-label="Zoom"
            />
            <Box
              component="button"
              type="button"
              aria-label="Zoom in"
              onClick={() => setZoom(Math.min(4, zoom + 0.15))}
              sx={{
                ...resetButton,
                color: 'text.secondary',
                width: 20,
                typography: 'body1',
              }}>
              +
            </Box>
          </Box>

          <Typography
            variant="caption"
            component="p"
            fontWeight={600}
            sx={{ color: quality.color }}>
            {quality.label}
          </Typography>
        </Box>
      </SmallModal>
    </>
  );
}

export default LogoSection;
